import React, { useCallback, useEffect, useRef, useState } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import ShowInscreenGraphic from "./ShowInscreenGraphic";
import ShowSize from "./ShowSize";
import SaleSheetSlide from "../SaleSheetSlide/SaleSheetSlide";
import {
    Flex,
    Box,
    Text,
    Image,
    Button,
    Collapse,
    useDisclosure,
    Container,
    HStack,
    Grid,
    GridItem,
} from "@chakra-ui/react";
import ShowOrientation from "./ShowOrientation";
import ShowPixelPitch from "./ShowPixelPitch";

function getPreviewImage(series, product, img, orientation) {
    const key = img + orientation;
    const bySize = series.imageMappingBySize?.[product];
    if (bySize?.[key]) return bySize[key];
    return series.imageMapping[key];
}

function getPixelPitchOptions(series, selectedSizeModel) {
    if (!series.pixelPitchBySize) return series.pixelPitch || [];

    return series.pixelPitchBySize[selectedSizeModel] || [];
}

function getSizeModelForProduct(series, product) {
    if (!series.pixelPitchBySize) return series.size?.[0]?.model || product;

    const selectedSize = series.size?.find((option) => {
        const options = series.pixelPitchBySize[option.model] || [];
        return (
            option.model === product ||
            options.some(
                (pitchOption) =>
                    !pitchOption.disabled && pitchOption.model === product,
            )
        );
    });

    return selectedSize?.model || series.size?.[0]?.model || product;
}

const ProductCard = ({
    series,
    countryList,
    defaultArModel,
    seriesName,
    closeCardEvent,
    viewboardList,
}) => {
    const [img, setImg] = useState(
        series.hideInscreenGraphic ? "" : Object.keys(series.imageMapping)[0],
    );
    const [product, setProduct] = useState(defaultArModel);
    const [modelSrc, setModelSrc] = useState("");
    const [arAvailable, setArAvailable] = useState(true);
    const [arModes, setArModes] = useState("webxr scene-viewer quick-look");
    const [isArButtonPressed, setIsArButtonPressed] = useState(false);
    const [orientation, setOrientation] = useState("");
    const [selectedSizeModel, setSelectedSizeModel] = useState(() =>
        getSizeModelForProduct(series, defaultArModel),
    );
    const cardRef = useRef(null);
    const modelViewerRef = useRef(null);

    const {
        isOpen: isCardOpen,
        onOpen: onCardOpen,
        onClose: onCardClose,
    } = useDisclosure();
    const {
        isOpen: isSlideOpen,
        onOpen: onSlideOpen,
        onClose: onSlideClose,
    } = useDisclosure();

    function handleClickCard() {
        if (isCardOpen) onCardClose();
        else onCardOpen();

        closeCardEvent(isCardOpen ? null : onCardClose);
    }

    const showArButtonSlot = useCallback(() => {
        const modelViewer = modelViewerRef.current;
        if (modelViewer && modelViewer.shadowRoot) {
            const arButtonSlot =
                modelViewer.shadowRoot.querySelector(".slot.ar-button");

            if (arButtonSlot) arButtonSlot.style.display = "block";
        }
    }, []);

    function CheckArAvailable() {
        const modelViewer = modelViewerRef.current;
        if (modelViewer && modelViewer.shadowRoot) {
            const arButtonSlot =
                modelViewer.shadowRoot.querySelector(".slot.ar-button");

            if (arButtonSlot) {
                const isEnabled = arButtonSlot.classList.contains("enabled");
                if (!isEnabled) {
                    // console.log('AR button is disabled');
                    arButtonSlot.style.display = "block";
                }
                if (isEnabled) setArAvailable(true);
            }
        }
    }

    const resetArButtonState = useCallback(() => {
        setIsArButtonPressed(false);
        setArAvailable(true);
        showArButtonSlot();
        requestAnimationFrame(showArButtonSlot);
        setTimeout(showArButtonSlot, 100);
    }, [showArButtonSlot]);

    function getOrientationOptionsById(id) {
        const item = series?.orientation?.find((element) => element.id === id);
        return item ? item.options : null;
    }

    function handleSizeChange(model) {
        const pitchOptions = series.pixelPitchBySize?.[model];
        const defaultPitchModel = pitchOptions?.find((option) => !option.disabled)?.model;

        setSelectedSizeModel(model);
        setProduct(defaultPitchModel || model);
    }

    function handleArButtonClick() {
        setIsArButtonPressed(true);
        showArButtonSlot();
        requestAnimationFrame(showArButtonSlot);
        setTimeout(showArButtonSlot, 100);
        setTimeout(resetArButtonState, 3000);
    }

    useEffect(() => {
        if (isCardOpen && cardRef.current) {
            setTimeout(async () => {
                cardRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                });
                CheckArAvailable();
            }, 250);
        }
    }, [isCardOpen]);

    // 只在打開 SaleSheetSlide 時寫入 localStorage，避免多個 ProductCard 互相覆蓋
    function handleSlideOpen() {
        localStorage.setItem(
            "prodcut_img",
            getPreviewImage(series, product, img, orientation) ?? "",
        );
        localStorage.setItem("product", product.substring(1));
        localStorage.setItem("model_name", product);
        localStorage.setItem("type", img === "crop" ? "corp" : "edu");
        onSlideOpen();
    }

    useEffect(() => {
        if (series.hideInscreenGraphic) setImg("");
        else setImg(Object.keys(series.imageMapping)[0]);
        setOrientation("");
        setSelectedSizeModel(getSizeModelForProduct(series, defaultArModel));
        setProduct(defaultArModel);
    }, [series, defaultArModel]);

    useEffect(() => {
        if (isCardOpen) {
            const model =
                product + (series.labelfeet ? "F" : "") + img + orientation;
            const modelData = viewboardList.find(
                ([key, value]) => key === model.slice(1, model.length),
            );
            setModelSrc(modelData ? modelData[1] : "");
        }
    }, [isCardOpen, img, product, orientation, series.labelfeet, viewboardList]);

    useEffect(() => {
        setIsArButtonPressed(false);
    }, [modelSrc, resetArButtonState]);

    useEffect(() => {
        const modelViewer = modelViewerRef.current;
        if (!modelViewer) return undefined;

        function handleModelLoad() {
            resetArButtonState();
        }

        function handleModelError() {
            resetArButtonState();
        }

        function handleArStatus(event) {
            const status = event.detail?.status;
            if (
                status === "session-started" ||
                status === "not-presenting" ||
                status === "failed"
            ) {
                resetArButtonState();
            }
        }

        modelViewer.addEventListener("load", handleModelLoad);
        modelViewer.addEventListener("error", handleModelError);
        modelViewer.addEventListener("ar-status", handleArStatus);

        return () => {
            modelViewer.removeEventListener("load", handleModelLoad);
            modelViewer.removeEventListener("error", handleModelError);
            modelViewer.removeEventListener("ar-status", handleArStatus);
        };
    }, [modelSrc, resetArButtonState]);

    useEffect(() => {
        function handlePageReturn() {
            resetArButtonState();
        }

        window.addEventListener("focus", handlePageReturn);
        window.addEventListener("pageshow", handlePageReturn);
        document.addEventListener("visibilitychange", handlePageReturn);

        return () => {
            window.removeEventListener("focus", handlePageReturn);
            window.removeEventListener("pageshow", handlePageReturn);
            document.removeEventListener("visibilitychange", handlePageReturn);
        };
    }, [resetArButtonState]);

    useEffect(() => {
        const userAgent =
            navigator.userAgent || navigator.vendor || window.opera;

        // if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        //     setArModes("webxr scene-viewer quick-look");
        // }
        // Android Chrome uses Scene Viewer (ARCore), not ARKit — quick-look is iOS-only
        if (/android/i.test(userAgent)) {
            setArModes("scene-viewer");
        }
    }, []);

    const pixelPitchOptions = getPixelPitchOptions(series, selectedSizeModel);

    return (
        <Flex
            bg="white"
            h="full"
            px="14px"
            py="15px"
            direction="column"
            shadow="md"
            boxShadow="0px 2px 12px 0px #00000040"
            borderRadius="8px"
            borderColor={isCardOpen ? "#FED141" : "gray.200"}
            borderWidth="1px"
            ref={cardRef}
        >
            <Flex direction="column" h="auto">
                <Flex as="button" onClick={handleClickCard}>
                    <Flex direction="row" w="100%" minH={"90px"}>
                        <HStack
                            flex="1"
                            h="100%"
                            p={["6px", "15px"]}
                            spacing={["2px", "50px"]}
                            justifyContent={"center"}
                        >
                            <Grid
                                // minW={["100px", "200px"]}
                                maxW="1080px"
                                templateColumns="repeat(2, 1fr)"
                                gap={4}
                            >
                                <GridItem colSpan={1}>
                                    <Flex
                                        h="100%"
                                        position="relative"
                                        justifyContent="left"
                                    >
                                        <Text
                                            fontSize={["17.5px", "18px"]}
                                            fontFamily="Open Sans"
                                            lineHeight="22px"
                                            textAlign="left"
                                            alignSelf="center"
                                            m="0px"
                                            mb={["10px", "0px"]}
                                        >
                                            {seriesName ? (
                                                <>
                                                    {seriesName}
                                                    <br />
                                                    {series?.name}
                                                </>
                                            ) : (
                                                series?.name
                                            )}
                                        </Text>
                                        {!isCardOpen && (
                                            <Text
                                                position="absolute"
                                                bottom={0}
                                                fontSize={["10px", "12px"]}
                                                fontWeight="600"
                                                lineHeight="14px"
                                                color="#B2B2B2"
                                                textAlign="left"
                                                textTransform="uppercase"
                                                letterSpacing="1px"
                                                p={0}
                                                m={0}
                                            >
                                                Tap to expand
                                            </Text>
                                        )}
                                    </Flex>
                                </GridItem>
                                <GridItem colSpan={1}>
                                    <Flex
                                        justifyContent={"flex-end"}
                                        minW={[
                                            "122px",
                                            "200px",
                                            "414px",
                                            "536px",
                                            "626px",
                                        ]}
                                        maxH="120px"
                                    >
                                        <Image
                                            w="100%"
                                            h="auto"
                                            objectFit="contain"
                                            src={getPreviewImage(
                                                series,
                                                product,
                                                img,
                                                orientation,
                                            )}
                                        />
                                    </Flex>
                                </GridItem>
                            </Grid>
                        </HStack>
                    </Flex>
                </Flex>

                <Collapse
                    in={isCardOpen}
                    animateOpacity
                    style={{ paddingLeft: 0, paddingRight: 0 }}
                >
                    <Flex
                        align="stretch"
                        direction="column"
                        p="15px"
                        gap="20px"
                    >
                        {!series.hideInscreenGraphic && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap="8px"
                                borderTop="1px solid #F2F2F2"
                            >
                                <Box h="38px" py="10px">
                                    <Text
                                        fontSize="18px"
                                        fontWeight="400"
                                        m="0px"
                                        color="#404041"
                                    >
                                        Select inscreen graphic
                                    </Text>
                                </Box>
                                <ScrollContainer>
                                    <ShowInscreenGraphic
                                        inscreenGraphicOptions={
                                            series?.inscreen_graphic
                                        }
                                        value={img}
                                        onChange={setImg}
                                    />
                                </ScrollContainer>
                            </Box>
                        )}
                        {series?.orientation && (
                            <Box borderTop="1px solid #F2F2F2"
                            display="flex"
                            flexDirection="column"
                            gap="8px"
                            >
                                <Box h="38px" py="10px">
                                    <Text
                                        fontSize="18px"
                                        fontWeight="400"
                                        m="0px"
                                        color="#404041"
                                    >
                                        Screen Orientation
                                    </Text>
                                </Box>
                                <ScrollContainer>
                                    <ShowOrientation
                                        orientationOptions={getOrientationOptionsById(
                                            img,
                                        )}
                                        defaultOption={""}
                                        onClickHandler={setOrientation}
                                    />
                                </ScrollContainer>
                            </Box>
                        )}
                        {!series?.hideSize && (
                            <Box
                                borderTop="1px solid #F2F2F2"
                                gap="8px"
                                flexDirection="column"
                                display="flex"
                            >
                                <Box h="38px" py="10px">
                                    <Text
                                        fontSize="18px"
                                        fontWeight="400"
                                        m="0px"
                                        color="#404041"
                                    >
                                        Select Size
                                    </Text>
                                </Box>
                                <ShowSize
                                    series={series}
                                    onSizeChange={handleSizeChange}
                                />
                            </Box>
                        )}
                        {pixelPitchOptions.length > 0 && (
                            <Box
                                borderTop="1px solid #F2F2F2"
                                display="flex"
                                flexDirection="column"
                                gap="8px"
                            >
                                <Box h="38px" py="10px">
                                    <Text
                                        fontSize="18px"
                                        fontWeight="400"
                                        m="0px"
                                        color="#404041"
                                    >
                                        Pixel Pitch (mm)
                                    </Text>
                                </Box>
                                <ShowPixelPitch
                                    options={pixelPitchOptions}
                                    product={product}
                                    onProductChange={setProduct}
                                />
                            </Box>
                        )}
                        <Box borderTop="1px solid #F2F2F2">
                            <Flex
                                direction="column"
                                gap="10px"
                                alignItems="center"
                                w={["full", "fit-content"]}
                                m="auto"
                                py="15px"
                            >
                                <Container w="237px" h="48px" p={0}>
                                    <model-viewer
                                        ref={modelViewerRef}
                                        src={
                                            modelSrc
                                                ? new URL(
                                                      modelSrc,
                                                      window.location.href,
                                                  ).href
                                                : ""
                                        }
                                        ar
                                        style={{
                                            arStatus: "session-started",
                                        }}
                                        ar-placement="wall"
                                        ar-modes={arModes}
                                        reveal="interaction"
                                        camera-controls
                                        ar-tracking="tracking"
                                        ar-scale="fixed"
                                        alt="A 3D model of some wall art"
                                    >
                                        <Button
                                            slot="ar-button"
                                            colorScheme="red"
                                            backgroundColor="#DB0025"
                                            minW={"100px,230px"}
                                            w="237px"
                                            h="48px"
                                            borderRadius="12px"
                                            fontWeight={600}
                                            textTransform="uppercase"
                                            fontFamily="Open Sans"
                                            fontSize="12px"
                                            letterSpacing="1px"
                                            textAlign="center"
                                            bg={
                                                isArButtonPressed
                                                    ? "#A9001C"
                                                    : "#DB0025"
                                            }
                                            borderColor={
                                                isArButtonPressed
                                                    ? "#7A0014"
                                                    : "#DB0025"
                                            }
                                            boxShadow={
                                                isArButtonPressed
                                                    ? "inset 0 2px 6px rgba(0, 0, 0, 0.35)"
                                                    : "none"
                                            }
                                            transform={
                                                isArButtonPressed
                                                    ? "translateY(1px)"
                                                    : "none"
                                            }
                                            _hover={{
                                                bg: isArButtonPressed
                                                    ? "#A9001C"
                                                    : "#DB0025",
                                            }}
                                            _active={{
                                                bg: "#A9001C",
                                                boxShadow:
                                                    "inset 0 2px 6px rgba(0, 0, 0, 0.35)",
                                                transform: "translateY(1px)",
                                            }}
                                            isDisabled={!arAvailable}
                                            onClick={handleArButtonClick}
                                        >
                                            Place on wall
                                        </Button>
                                        <div slot="progress-bar"></div>
                                    </model-viewer>
                                </Container>

                                <Button
                                    borderColor="#B2B2B2"
                                    variant="outline"
                                    textTransform="uppercase"
                                    fontFamily="Open Sans"
                                    fontSize="12px"
                                    borderRadius="12px"
                                    letterSpacing="1px"
                                    onClick={handleSlideOpen}
                                    minW={"100px,230px"}
                                    w="237px"
                                    h="48px"
                                >
                                    Send Custom Sales Sheet
                                </Button>
                                <Button
                                    textColor="black"
                                    borderColor="#B2B2B2"
                                    variant="outline"
                                    borderRadius="12px"
                                    isDisabled
                                    textTransform="uppercase"
                                    fontFamily="Open Sans"
                                    fontSize="12px"
                                    letterSpacing="1px"
                                    minW={"100px,230px"}
                                    w="237px"
                                    h="48px"
                                >
                                    Request a demo
                                </Button>
                            </Flex>
                        </Box>
                    </Flex>
                </Collapse>

                <SaleSheetSlide
                    isOpen={isSlideOpen}
                    onClose={onSlideClose}
                    modelName={product}
                    countryList={countryList}
                />
            </Flex>
        </Flex>
    );
};

export default ProductCard;
